'use client'

import { useEffect, useState } from 'react'
import Card from '../ui/card'
import { IconCheckMarkBadgeSvg } from '~/icons/checkmark-badge-02'
import { IconErrorXSvg } from '~/icons/error-x'
import { IconStarSvg } from '~/icons/star'
import { IconChevronRightSmallSvg } from '~/icons/chevron-right-small'
import { formatPrice } from '~/utils/format'
import dayjs from 'dayjs'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { useBookingCart } from '~/stores/booking-cart'
import { useSearchParams } from 'next/navigation'
import { PaymentStatus } from './enum'
import { usePaymentData } from '~/hooks/use-payment-data'
import { useVerifyOrder } from '~/hooks/use-verify-order'
import { useCancelBooking } from '~/hooks/use-booking'
import { BookingStep, useBookingFlow } from '~/stores/booking-flow'
import { useLoyaltySession } from '~/hooks/use-loyalty-session'
import { useLoyaltyNotice } from '~/hooks/use-loyalty-notice'

const HOME = process.env.NEXT_PUBLIC_PORTAL_URL
const LOYALTY_MEMBER_URL = process.env.NEXT_PUBLIC_LOYALTY_MEMBER_URL

interface Props {
  dataSocket?: any
  paymentData?: IPaymentData
  transactionResults: (status: string) => void
  onCloseAction: () => void
}

interface IPaymentData {
  bookingNo: string
  fullName: string
  customerName: string
  totalAmount: number
  arrivalDate: string
  departureDate: string
  voucherCode?: string
  cardItems: any
}

export default function Step3({ dataSocket, transactionResults, onCloseAction }: Props) {
  const [dataSocketPayment, setDataSocketPayment] = useState(dataSocket || null)
  const [paymentResults, setPaymentResults] = useState<PaymentStatus | ''>('')
  const [totalAdult, setTotalAdult] = useState(0)
  const [totalChild, setTotalChild] = useState(0)
  const { paymentData } = usePaymentData()
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const searchParams = useSearchParams()
  const hotelId = searchParams.get('hotelId')
  const bookingNo = searchParams.get('bookingNo')
  const totalAmount = searchParams.get('totalAmount')
  const arrivalDate = searchParams.get('checkInDate')
  const departureDate = searchParams.get('checkOutDate')
  const customerName = searchParams.get('customerName')
  const adults = searchParams.get('adults')
  const kidsOver6 = searchParams.get('kidsOver6')
  const kidsUnder6 = searchParams.get('kidsUnder6')
  const epayResult = searchParams.get('epayResult')
  const againUrl = searchParams.get('againUrl')
  const { verifyOrder } = useVerifyOrder()
  const { setCurrentStep, setCurrentPaymentStep } = useBookingFlow()

  const { clearCart } = useBookingCart()
  const { clearPaymentData } = usePaymentData()
  const { cancelBooking } = useCancelBooking()
  const { isLoggedIn } = useLoyaltySession()
  // The notice response already folds viewer_authenticated into `state`
  // (LOYAL-16) — branch this box off `state`, not the client session flag:
  // NOT_MEMBER → guest must register/log in (show earnPointsDesc);
  // ESTIMATED/POSTED/PROCESSING → member matched, no desc.
  const { data: loyaltyNotice } = useLoyaltyNotice({
    memberPhone: paymentData?.phone,
    hotelId,
    amountVnd: Number(paymentData?.totalAmount || totalAmount) || null,
    viewerAuthenticated: isLoggedIn,
    enabled: paymentResults === PaymentStatus.SUCCESS,
  })
  // Loyalty Member portal doesn't expose dedicated REGISTER / POINTS_OVERVIEW pages yet
  // (LOYAL-16) — every cta.target routes to the member login entry point for now; branch
  // on `target` here once those routes ship.
  const handleEarnPointsCta = () => {
    if (!LOYALTY_MEMBER_URL) {
      console.error('NEXT_PUBLIC_LOYALTY_MEMBER_URL is not set — cannot navigate to Loyalty.')
      return
    }
    window.location.href = LOYALTY_MEMBER_URL + '?tab=points'
  }

  const handleRedirect = (target: 'home' | '', status: PaymentStatus) => {
    if (status === PaymentStatus.FAIL) {
      cancelBooking({
        hotelId: Number(hotelId),
        bookingId: paymentData?.bookingNo || '',
        reason: 'User cancelled the booking',
      })
    }
    setCurrentPaymentStep(1)
    setCurrentStep(BookingStep.LIST)
    clearCart()
    window.sessionStorage.removeItem('voucherCode')
    const params = new URLSearchParams()
    params.set('hotelId', hotelId || '')
    params.set('checkInDate', paymentData?.arrivalDate || '')
    params.set('checkOutDate', paymentData?.departureDate || '')
    params.set('adults', String(paymentData?.cardItems?.[0]?.guests?.adults || 1))
    params.set('kidsOver6', String(paymentData?.cardItems?.[0]?.guests?.children6To12 || 0))
    params.set('kidsUnder6', String(paymentData?.cardItems?.[0]?.guests?.childrenUnder6 || 0))
    params.set('bookingNo', paymentData?.bookingNo || '')
    params.set('totalAmount', String(paymentData?.totalAmount || 0))
    params.set('customerName', paymentData?.customerName || '')
    const url = target === 'home' ? `${HOME}/${locale}` : `/?${params.toString()}`
    window.location.href = url
  }

  // Handle socket data result
  useEffect(() => {
    if (!dataSocketPayment) return

    let parsedData: any = dataSocketPayment
    if (typeof dataSocketPayment === 'string') {
      try {
        parsedData = JSON.parse(dataSocketPayment)
      } catch (err) {
        console.error('Failed to parse dataSocketPayment:', err)
        return
      }
    }

    const contentObj = parsedData.Content
    let contentData = contentObj
    if (typeof contentObj === 'string') {
      try {
        contentData = JSON.parse(contentObj)
      } catch (err) {
        console.error('Failed to parse Content:', err)
        return
      }
    }

    const status = contentData?.Data?.BookingStatus || PaymentStatus.FAIL
    setPaymentResults(status)
    transactionResults(status)
    window.sessionStorage.removeItem('voucherCode')
  }, [dataSocketPayment])

  // Sync dataSocketPayment when the dataSocket prop changes (e.g. from socket or debug button)
  useEffect(() => {
    setDataSocketPayment(dataSocket)
  }, [dataSocket])

  // Handle initial data + fallback when no socket (runs once on mount only)
  useEffect(() => {
    if (paymentData?.cardItems?.length) {
      const adultCount = paymentData.cardItems.reduce((sum, obj) => sum + obj.guests.adults, 0)
      const childCount = paymentData.cardItems.reduce(
        (sum, obj) => sum + obj.guests.children6To12 + obj.guests.childrenUnder6,
        0
      )
      setTotalAdult(adultCount)
      setTotalChild(childCount)
    }
    window.sessionStorage.removeItem('voucherCode')

    if (!dataSocket) {
      if (epayResult) {
        verifyOrder(epayResult)
          .then((result) => {
            if (result.transStatus === 4) {
              setCurrentStep(BookingStep.PAYMENT)
            } else {
              const status = result.transStatus === 1 ? PaymentStatus.SUCCESS : PaymentStatus.FAIL
              setPaymentResults(status)
              transactionResults(status)
              // Chỉ clear payment data khi không phải trường hợp cancel (transStatus !== 4)
              // clearPaymentData()
            }
          })
          .catch((error) => {
            console.error('Failed to verify payment:', error)
            setPaymentResults(PaymentStatus.FAIL)
            transactionResults(PaymentStatus.FAIL)
            clearPaymentData()
          })
      }
      // else {
      //   clearPaymentData()
      // }
    }
  }, [])

  return (
    <Card className="xs:mb-4 max-w-[520px] p-4 pt-4" padding={0}>
      <div className="mx-auto w-full">
        {paymentResults === PaymentStatus.SUCCESS && (
          <>
            <div className="mb-6 grid justify-center gap-3 text-center">
              <span className="mx-auto">
                <IconCheckMarkBadgeSvg />
              </span>
              <p className="text-center text-[20px] font-medium text-secondary-950">
                {t('payment.successTitle')}{' '}
                {formatPrice(Number(paymentData?.totalAmount || totalAmount))}
              </p>
            </div>
            {loyaltyNotice && (
              <div className="mb-6 flex items-start rounded-[10px] border border-[#A8824A] bg-primary-50 p-[14px]">
                <div
                  className={`flex w-full ${isLoggedIn || (!isLoggedIn && loyaltyNotice.state !== 'NOT_MEMBER') ? 'flex-row justify-between' : 'flex-col'} gap-1`}
                >
                  <div className="flex w-full justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex size-[25px] shrink-0 items-center justify-center rounded-full bg-[#7A5128]">
                        <IconStarSvg />
                      </span>
                      <p className="text-[16px] font-semibold text-[#2E2924]">
                        {t(
                          isLoggedIn || (!isLoggedIn && loyaltyNotice.state !== 'NOT_MEMBER')
                            ? 'payment.earnPointsEstimatedTitle'
                            : 'payment.earnPointsTitle',
                          { value: loyaltyNotice.points }
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleEarnPointsCta}
                      className="flex h-[30px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded bg-[#7A5128] px-3 text-[13px] font-medium text-white md:hidden"
                    >
                      {t('payment.earnPointsCta')}
                      <IconChevronRightSmallSvg />
                    </button>
                  </div>
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                    {!isLoggedIn && loyaltyNotice.state === 'NOT_MEMBER' && (
                      <p className="flex-1 text-[13px] italic text-[#635C54]">
                        {t('payment.earnPointsDesc')}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleEarnPointsCta}
                      className="hidden h-[30px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded bg-[#7A5128] px-3 text-[13px] font-medium text-white md:flex"
                    >
                      {t('payment.earnPointsCta')}
                      <IconChevronRightSmallSvg />
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="grid gap-3">
              <p className="flex items-center justify-between gap-8 text-[14px]">
                <span className="text-[#666B74]">{t('payment.customerName')}:</span>
                <strong>{paymentData?.customerName || customerName}</strong>
              </p>
              <p className="flex justify-between text-[14px]">
                <span className="text-[#666B74]">{t('payment.bookingNo')}:</span>
                <strong>{paymentData?.bookingNo || bookingNo}</strong>
              </p>
              <p className="flex justify-between text-[14px]">
                <span className="text-[#666B74]">{t('payment.stayDate')}:</span>
                <strong>
                  {dayjs(paymentData?.arrivalDate || arrivalDate).format('DD/MM/YYYY')} -{' '}
                  {dayjs(paymentData?.departureDate || departureDate).format('DD/MM/YYYY')}
                </strong>
              </p>
              <p className="flex justify-between text-[14px]">
                <span className="text-[#666B74]">{t('payment.guestCount')}:</span>
                <strong>
                  {totalAdult || adults} {t('booking.adults')} -{' '}
                  {totalChild || Number(kidsOver6) + Number(kidsUnder6) || 0}{' '}
                  {t('booking.children')}
                </strong>
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => handleRedirect('home', PaymentStatus.SUCCESS)}
                className="flex w-full items-center justify-center gap-2 rounded bg-[#EBE3D6] p-3 text-base text-typo-brand"
              >
                {t('payment.backToHome')}
              </button>
              <button
                onClick={() => handleRedirect('', PaymentStatus.SUCCESS)}
                className="flex w-full items-center justify-center gap-2 rounded bg-primary-500 p-3 text-base text-white"
              >
                {t('payment.continueBooking')}
              </button>
            </div>
          </>
        )}
        {paymentResults === PaymentStatus.FAIL && (
          <>
            <div className="mb-3 grid justify-center text-center">
              <span className="mx-auto mb-3">
                <IconErrorXSvg />
              </span>
              <label className="my-1 text-[20px] font-medium">{t('payment.failed')}</label>
              <label className="mb-1 text-sm font-medium text-secondary-500">
                {t('payment.failedMsg')}
                <b className="text-[#2D2E32]"> {paymentData?.bookingNo || bookingNo}</b>
              </label>
              <label className="text-sm font-light text-secondary-500">
                {t('payment.contactSupport')} <b className="text-primary-500">1900 1901</b>{' '}
                {t('payment.forSupport')}
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  clearPaymentData()
                  handleRedirect('home', PaymentStatus.FAIL)
                }}
                className="flex w-full items-center justify-center gap-2 rounded bg-[#EBE3D6] p-3 text-base text-typo-brand"
              >
                {t('payment.backToHome')}
              </button>
              <button
                onClick={() => {
                  // clearPaymentData()
                  onCloseAction()
                }}
                className="flex w-full items-center justify-center gap-2 rounded bg-primary-500 p-3 text-base text-white"
              >
                {t('payment.retryPayment')}
              </button>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}
